import { create } from "zustand";
import { LiveKitData, LiveKitRoomManager } from "../../dth/call/LiveKitRoomManager";
import { CallKeyExchangeCompletedInfo, CallKeyExchangeManagerVersion, getCallKeyExchangeManagerInstance, ICallKeyExchangeManager } from "../../dth/call/commons/CallKeyExchangeManagerFactory";
import { DthSessionStorage } from "../../dth/clientHttp/DthSessionStorage";
import { DthHttpClient } from "../../dth/clientHttp/DthHttpClient";
import { bytesToBase64, generateSecureRandomBytes } from "../../dth/call/commons/cryptoUtils";
import { Action } from "../../dispatcher/actions";
import defaultDispatcher from "../../dispatcher/dispatcher";
import { API_MATRIX_NAME_KEY } from "../../dth/constants";
import { IDTHEngagementRoom, IDTHEngagementRoomResponse } from "../../dth/clientHttp/models/httpClientModels";
import { Room } from "matrix-js-sdk/src/matrix";
import { SoundManager } from "../../audio/ringControl";
import { useEffect } from "react";
import { /*Room as LiveKitRoom,*/ Participant, PublishTrackError } from "livekit-client";
import { _t } from "../../languageHandler";
import mxNotifier from "../../Notifier";
import { MatrixClientPeg } from "../../MatrixClientPeg";
import { matrixIdToParticipantId, toWssUrl } from "../../dth/clientHttp/utils/utils";
import ErrorDialog from "../../components/views/dialogs/ErrorDialog";
import Modal from "../../../src/Modal";
import { checkMicrophonePermissionsGranted, findRoomByMatrixName } from "../../dth/utils";
import HttpResponseError from "../../dth/clientHttp/errors/HttpResponseError";

/**
 * Versione corrente del CallKeyExchangeManager
 */
const CALL_VERSION = CallKeyExchangeManagerVersion.V2;

//#region UTILS

/**
 * Metadata della stanza di chiamata, 
 * contiene sia info per la gestione della chiamata (LiveKit) che info per la UI (Matrix)
 */
export interface CallRoomMetadata {
    lkUrl: string;
    lkToken: string;
    lkRoomManager: LiveKitRoomManager;
    matrixRoom: Room | null;
    engagementRoom: IDTHEngagementRoom;
}

/**
 * Stati della chiamata
 */
export enum CallStatus {
    idle = "idle",                          // nessuna chiamata attiva
    incomingRinging = "incoming-ringing",   // chiamata in arrivo
    outgoingRinging = "outgoing-ringing",   // chiamata in uscita
    connecting = "connecting",              // sto creando/joinando la stanza
    waiting = "waiting",                    // in attesa di altri partecipanti
    inCall = "in-call",                     // chiamata attiva con almeno 2 partecipanti
    ended = "ended",                        // chiamata terminata
    error = "error"                         // errore nella chiamata
}

/**
 * Motivi per cui una chiamata è stata rifiutata o persa
 */
export enum CallRejectReason {
    missed = "missed",        // chiamata persa
    refused = "refused",      // chiamata rifiutata
}

/**
 * Motivi per cui una chiamata è stata terminata
 */
export enum CallLeaveReason {
    noresponse = "noresponse",// chiamata senza risposta (timeout)
    refused = "refused",      // chiamata rifiutata
    exited = "exited",        // uscita volontaria dall'altro utente dalla chiamata (chiamata 1 a 1)
    // ended = "ended",          // chiamata terminata normalmente
}

export interface CallDispatchPayload {
    action: Action.ViewRoom;
    room_id: string | null;
    showChatInCall?: boolean;
    fallbackToHome?: boolean;
    /**
     * Attenzione!
     * (undefined !== null)
     *  Se undefined viene ignorato;
     *  Se null viene forzato a null (utile per resettare il liveKitData quando si lascia la chiamata)
     */
    liveKitData?: LiveKitData | null;
}

//#endregion

export interface CallState {
    callRoom: CallRoomMetadata | null;
    /**
     * Set di id dei partecipanti alla chiamata (escluso se stesso, solo username @{username}:domain),
     * utilizzato principalmente per mostrare i nomi dei partecipanti nella UI
     */
    otherParticipantIds: Set<string>;
    /**
     * Set di id dei partecipanti attualmente presenti nella chiamata (solo username @{username}:domain)
     */
    presentParticipantIds: Set<string>;
    /**
     * Nome del chiamante (per chiamate in arrivo) 
     */
    callerName: string | null;
    roomId: string | null;
    inCall: boolean;
    start: number | null;
    end: number | null;
    isVideo: boolean;
    micOn: boolean;
    camOn: boolean;
    channelsManager: ICallKeyExchangeManager[];
    pip: boolean;
    pipWindow: Window | null;
    canShowPiP: boolean;
    callStatus: CallStatus;
    showChatInCall: boolean;
    invitationId?: string | null;
    isGroupCall?: boolean | null;
    isIncomingCall?: boolean | null;
    setCallRoom: (callRoom: CallRoomMetadata | null) => void;
    setOtherParticipantIds: (participantIds: Set<string>) => void;
    setPresentParticipantIds: (participantIds: Set<string>) => void;
    addPresentParticipant: (participant: Participant) => void;
    removePresentParticipant: (participant: Participant) => void;
    setPresentParticipants: (participants: Participant[], includeCurrentUser?: boolean) => void;
    setCallerName: (callerName: string | null) => void;
    setRoomId: (roomId: string | null) => void;
    setInCall: (inCall: boolean) => void;
    setStart: (start: number | null) => void;
    setEnd: (end: number | null) => void;
    setIsVideo: (isVideo: boolean) => void;
    setMicOn: (micOn: boolean) => void;
    setCamOn: (camOn: boolean) => void;
    setChannelsManager: (channels: ICallKeyExchangeManager[]) => void;
    setPiP: (pip: boolean) => void;
    setPiPWindow: (pipWindow: Window | null) => void;
    togglePiP: (pip?: boolean) => void;
    refreshPiPMetadata: (pipWindow: Window | null, options?: { refreshOnlyTheme?: boolean }) => void;
    setCallStatus: (status: CallStatus, reason?: any) => void;
    setInvitationId: (invitationId: string | null) => void;
    createCall: (participantsId: string[], room?: Room) => Promise<void>;
    joinCall: (invitationId?: string) => Promise<boolean>;
    rejectCall: (invitationId?: string, callerName?: string, reason?: CallRejectReason, roomId?: string, showNotification?: boolean) => Promise<boolean>;
    leaveCall: (reason?: CallLeaveReason) => Promise<void>;
    toggleMic: () => Promise<void>;
    toggleCam: () => Promise<void>;
    triggerIncomingCall: (invitationId: string, callerName: string, roomId?: string) => void;
    setIsGroupCall: (isGroupCall: boolean | null) => void;
    setIsIncomingCall: (isIncomingCall: boolean | null) => void;
    dispatchCallAction: (payload: CallDispatchPayload) => void;
    setShowChatInCall: (showChatInCall: boolean, triggerDispatch?: boolean) => void;
    goToCallRoomView: () => void;
    resetCallState: () => void;
}

//#region Call control utils

// Suoneria di chiamata in arrivo
const ringSource: SoundManager = new SoundManager();

// Singleton per gestire i timeout nelle chiamate
let callbackCallTimeoutId: ReturnType<typeof setTimeout> | null = null;
// Timeout per chiamata senza risposta
const MISSED_CALL_TIMEOUT_MS = 30000; // 30 secondi
// Timeout per connessione alla chiamata durante la fase di connecting (prima di entrare in chiamata)
const CONNECTING_CALL_TIMEOUT_MS = 5000; // 5 secondi
// Timeout per di attesa di altri partecipanti durante la fase di waiting dopo essere già entrato in call in caso di chiamata 1:1
// (se è una chiamata di gruppo, non scatta questo timeout perché si aspetta che entrino altri partecipanti anche dopo essere entrati in chiamata)
const WAITING_1_1_CALL_TIMEOUT_MS = 5000; // 5 secondi


/**
 * Callback da eseguire dopo il timeout specificato in input
 * Se già attivo, lo resetta.
 * @param onTimeout funzione da chiamare allo scadere
 * @param timeoutMs durata in ms del timeout. Se 0 o negativa, non imposta un timeout.
 */
export function startCallbackTimeout(onTimeout: () => void, timeoutMs: number = MISSED_CALL_TIMEOUT_MS) {
    clearCallbackTimeout();
    if (timeoutMs <= 0) return; // Non impostare un timeout se la durata è 0 o negativa
    const timeoutId = setTimeout(() => {
        if (callbackCallTimeoutId === timeoutId) {
            onTimeout();
        }
    }, timeoutMs);
    callbackCallTimeoutId = timeoutId;
}

/**
 * Cancella il timeout per il callback, se attivo.
 */
export function clearCallbackTimeout() {
    if (callbackCallTimeoutId) {
        clearTimeout(callbackCallTimeoutId);
        callbackCallTimeoutId = null;
    }
}

//#endregion

export const useCallStore = create<CallState>((set, get) => ({
    callRoom: null,
    otherParticipantIds: new Set<string>(),
    presentParticipantIds: new Set<string>(),
    roomId: null,
    isVideo: false,
    inCall: false,
    start: null,
    end: null,
    micOn: true,
    camOn: true,
    channelsManager: [],
    pip: false,
    pipWindow: null,
    canShowPiP: ('documentPictureInPicture' in window) ? true : false,
    callStatus: CallStatus.idle,
    showChatInCall: false,
    callerName: null,
    invitationId: null,
    isGroupCall: null,
    isIncomingCall: null,
    setIsVideo: (isVideo) => set({ isVideo }),
    setInvitationId: (invitationId) => set({ invitationId: invitationId }),
    setRoomId: (roomId) => set({ roomId }),
    setCallRoom: (callRoom) => {
        set({ isVideo: callRoom?.engagementRoom.isVideo ?? false });
        set({ callRoom });
    },
    setOtherParticipantIds: (participantIds) => set({ otherParticipantIds: participantIds }),
    setPresentParticipantIds: (participantIds) => set({ presentParticipantIds: participantIds }),
    addPresentParticipant: (participant) => {
        console.debug("[useCallStore] Adding participant:", participant);
        const callState = get();

        // Ottieni l'ID del partecipante che sta entrando (solo username @{username}:domain) 
        const currentParticipantId = matrixIdToParticipantId(participant.identity);
        // crea una copia del set attuale
        const presentParticipantIds = new Set(callState.presentParticipantIds);
        const beforeSize = presentParticipantIds.size;
        presentParticipantIds.add(currentParticipantId); // rimuove prima per evitare duplicati
        const afterSize = presentParticipantIds.size;

        if (beforeSize !== afterSize) {
            callState.setPresentParticipantIds(presentParticipantIds);
            console.debug("[useCallStore] Current present participants before adding:", Array.from(presentParticipantIds));
        }
    },
    removePresentParticipant: (participant) => {
        console.debug("[useCallStore] Removing participant:", participant);
        const callState = get();

        // Ottieni l'ID del partecipante che sta lasciando (solo username @{username}:domain) 
        const currentParticipantId = matrixIdToParticipantId(participant.identity);

        // crea una copia del set attuale
        const presentParticipantIds = new Set(callState.presentParticipantIds);
        const beforeSize = presentParticipantIds.size;
        presentParticipantIds.delete(currentParticipantId); // rimuove prima per evitare duplicati
        const afterSize = presentParticipantIds.size;

        if (beforeSize !== afterSize) {
            callState.setPresentParticipantIds(presentParticipantIds);
            console.debug("[useCallStore] Current present participants after removing:", Array.from(presentParticipantIds));
        }
    },
    setPresentParticipants: (participants, includeCurrentUser = false) => {
        const callState = get();
        const participantIds = new Set(participants.map(p => matrixIdToParticipantId(p.identity)));
        if (includeCurrentUser) {
            const myMatrixId = DthSessionStorage.getGenericItem(API_MATRIX_NAME_KEY);
            if (myMatrixId) {
                participantIds.add(matrixIdToParticipantId(myMatrixId));
            }
        }

        callState.setPresentParticipantIds(participantIds);
        console.debug("[useCallStore] Current present participants set to:", Array.from(participantIds));
    },
    setCallerName: (callerName) => set({ callerName }),
    setIsGroupCall: (isGroupCall) => set({ isGroupCall }),
    setInCall: (inCall) => set({ inCall }),
    setStart: (start) => set({ start }),
    setEnd: (end) => set({ end }),
    setMicOn: (micOn) => set({ micOn }),
    setCamOn: (camOn) => set({ camOn }),
    setChannelsManager: (channelsManager) => set({ channelsManager }),
    setPiP: (pip) => set({ pip }),
    setPiPWindow: (pipWindow) => {
        // Se c'è già una finestra PiP aperta e 
        // diversa da quella che stiamo per impostare, chiudila
        const callState = get();
        const prev = callState.pipWindow;
        if (prev && prev !== pipWindow) {
            try {
                prev.close();
                // Se la chiamata è attiva, torna alla vista della chiamata dopo aver chiuso il PiP
                if (callState.inCall) {
                    callState.goToCallRoomView();
                }
            } catch { }
        }
        set({ pipWindow });
    },
    /**
     * Attiva/disattiva la modalità PiP. Se forzato (forceOn true/false), apre o chiude PiP di conseguenza.
     * Se non specificato, alterna lo stato attuale.
     */
    togglePiP: async (forceOn?: boolean) => {
        const callState = get();
        // Se forceOn è specificato, usalo per determinare se aprire o chiudere PiP. Altrimenti, alterna lo stato attuale.
        const shouldOpen = typeof forceOn === "boolean" ? forceOn : !callState.pip;
        // Per aprire PiP, verifica se la chiamata è attiva
        if (callState.inCall && shouldOpen) {
            // Le condizioni per aprire il PiP:
            // - Il browser supporta PiP
            // - Non c'è già una PiP window
            // - Stato chiamata deve essere valido
            const invalidStatuses = [
                // In chiamata in arrivo mostra il dialog per accettare o rifiutare la chiamata, il PiP non è ancora disponibile
                CallStatus.incomingRinging,
                // Chiamata non attiva, PiP non disponibile
                CallStatus.idle,
                // Chiamata in errore, sta per chiudersi, PiP non disponibile
                CallStatus.error,
                // Chiamata terminata, PiP non disponibile
                CallStatus.ended
            ];
            if (
                // Verifica se il browser supporta PiP
                callState.canShowPiP &&
                // Verifica che non ci sia già una finestra PiP aperta
                !callState.pipWindow &&
                // Verifica che lo stato della chiamata sia valido per aprire il PiP
                !invalidStatuses.includes(callState.callStatus)
            ) {
                try {
                    // @ts-ignore
                    const pw: Window = await window.documentPictureInPicture.requestWindow();

                    // Aggiorna il contenuto del PiP con il tema e gli stili correnti
                    callState.refreshPiPMetadata(pw, { refreshOnlyTheme: false });

                    // Aggiungi un listener per quando la finestra PiP viene chiusa manualmente dall'utente, 
                    // in modo da aggiornare lo stato di conseguenza
                    pw.addEventListener('pagehide', () => {
                        callState.setPiP(false);
                        callState.setPiPWindow(null);
                    });
                    callState.setPiPWindow(pw);
                    callState.setPiP(true);
                } catch (error) {
                    console.error("[useCallStore] Failed to enter Picture-in-Picture mode:", error);
                    callState.setPiP(false);
                    callState.setPiPWindow(null);
                }
            }
        } else {
            // Chiusura PiP: verifica se la finestra PiP è aperta prima di tentare di chiuderla
            if (callState.pip) {
                callState.setPiP(false);
            }
            if (callState.pipWindow) {
                callState.setPiPWindow(null);
            }
        }
    },
    /**
     * Aggiorna i metadati del PiP forzando un re-render della finestra PiP.
     * Utile ad esempio quando cambia il tema o lingua.
     * @param pipWindow finestra PiP da aggiornare, se null aggiorna la finestra PiP attualmente aperta nello stato.
     * @param refreshOnlyTheme se true, aggiorna solo i metadati relativi al tema (classi e stili), 
     * lasciando intatto il contenuto del PiP. 
     * Utile per evitare di interrompere eventuali video in riproduzione nel PiP quando si cambia tema. 
     * Di default è false, quindi aggiorna completamente il PiP.
     */
    refreshPiPMetadata: (pipWindow: Window | null = null, options: { refreshOnlyTheme?: boolean } = {}) => {
        const callState = get();
        const pw = pipWindow || callState.pipWindow;
        if (!pw) return;

        const { refreshOnlyTheme = false } = options;

        // Se refreshOnlyTheme è false, resetta completamente il contenuto del PiP per forzare un re-render completo, 
        // altrimenti aggiorna solo classi e stili per mantenere intatto il contenuto (es. video in riproduzione)
        if (!refreshOnlyTheme) {
            pw.document.body.style = "";
            pw.document.body.className = "";
            pw.document.head.innerHTML = "";

            // Copia tutti i fogli di stile e gli stili inline nell'head
            window.document.head
                .querySelectorAll('link[rel="stylesheet"], style')
                .forEach(node => pw.document.head.appendChild(node.cloneNode(true)));
        }

        // In entrambi i casi, aggiorna classi e stili per applicare il tema corrente al PiP

        // Copia tutte le classi dal body principale al body della PiP window
        pw.document.body.className = window.document.body.className;
        // Copia tutti gli stili inline dal body principale al body della PiP window
        for (const styleProp of window.document.body.style) {
            pw.document.body.style.setProperty(styleProp, window.document.body.style.getPropertyValue(styleProp));
        }
    },
    setIsIncomingCall: (isIncomingCall) => set({ isIncomingCall }),
    setCallStatus: (callStatus, reason) => {
        const callState = get();
        const prevStatus = callState.callStatus;
        console.debug("[useCallStore] Setting call status to:", callStatus);
        // Se lo stato non cambia, non fare nulla
        if (prevStatus === callStatus) {
            console.warn("[useCallStore] Call status is already", callStatus, "no update needed.");
            return;
        }

        //#region Ignora transizioni non valide
        // IDLE => ENDED: non è valido, la chiamata è già terminata, chiusura superflua
        if (prevStatus === CallStatus.idle && callStatus === CallStatus.ended) {
            console.warn("[useCallStore] Ignoring transition from idle to ended.");
            return;
        }

        // IDLE => ERROR: non è valido, la chiamata è già terminata, errori superflui
        if (prevStatus === CallStatus.idle && callStatus === CallStatus.error) {
            console.warn("[useCallStore] Ignoring transition from idle to error.");
            return;
        }
        //#endregion

        set({ callStatus });
        // Stati in cui la suoneria deve suonare
        switch (callStatus) {
            case CallStatus.idle:
                // Nessuna chiamata attiva

                // Reset stato chiamata
                callState.resetCallState();

                // Ferma tutte le suonerie in caso siano in riproduzione
                ringSource.stopAll(true);

                break;
            case CallStatus.incomingRinging:
                // Chiamata in arrivo
                // Avvia la suoneria se non è già in riproduzione
                ringSource.play("ingoingCall");

                // Imposta il timeout per chiamata senza risposta
                const initialInvitationId = callState.invitationId;
                startCallbackTimeout(async () => {
                    const newCallState = get();
                    // Se la chiamata è ancora in arrivo dopo il timeout, considerala persa
                    if (
                        newCallState.inCall &&
                        newCallState.callStatus === CallStatus.incomingRinging &&
                        newCallState.isIncomingCall &&
                        newCallState.invitationId === initialInvitationId
                    ) {
                        console.debug("[useCallStore] Chiamata senza risposta, la considero persa.", {
                            inCall: newCallState.inCall,
                            callStatus: newCallState.callStatus,
                            isIncomingCall: newCallState.isIncomingCall,
                            invitationId: newCallState.invitationId,
                            initialInvitationId
                        });
                        await newCallState.rejectCall(newCallState.invitationId || undefined, undefined, CallRejectReason.missed, newCallState.roomId || undefined);
                    }

                });
                break;
            case CallStatus.outgoingRinging:
                // Chiamata in uscita
                // Avvia la suoneria se non è già in riproduzione
                ringSource.play("outgoingCall");

                // Imposta il timeout per chiamata senza risposta
                startCallbackTimeout(async () => {
                    const newCallState = get();
                    // Se la chiamata è ancora in arrivo dopo il timeout, considerala persa
                    if (newCallState.inCall &&
                        newCallState.callStatus !== CallStatus.inCall &&
                        !newCallState.isIncomingCall) {
                        console.debug("[useCallStore] Chiamata senza risposta, la considero persa.", {
                            inCall: newCallState.inCall,
                            callStatus: newCallState.callStatus,
                            isIncomingCall: newCallState.isIncomingCall
                        });
                        await newCallState.leaveCall(CallLeaveReason.noresponse);
                    }
                });
                break;
            case CallStatus.connecting:
                // Sto creando/joinando la stanza

                // Ferma la suoneria di chiamata in entrata, se in riproduzione
                ringSource.stop("ingoingCall");

                // Imposta il timeout per connessione alla chiamata durante la fase di connecting (prima di entrare in chiamata)
                if (callState.isIncomingCall) {
                    const connectingInvitationId = callState.invitationId;
                    startCallbackTimeout(async () => {
                        const newCallState = get();
                        // Se la chiamata è ancora in arrivo dopo il timeout, considerala persa
                        if (
                            newCallState.inCall &&
                            newCallState.callStatus === CallStatus.connecting &&
                            newCallState.isIncomingCall &&
                            newCallState.invitationId === connectingInvitationId
                        ) {
                            console.debug("[useCallStore] Timeout durante la fase di connecting.", {
                                inCall: newCallState.inCall,
                                callStatus: newCallState.callStatus,
                                isIncomingCall: newCallState.isIncomingCall,
                                invitationId: newCallState.invitationId,
                                connectingInvitationId
                            });
                            // Tentativo di reject della chiamata per timeout durante connecting, se è ancora in connecting dopo il timeout e se è la stessa chiamata (stesso invitationId)
                            try {
                                await newCallState.rejectCall(newCallState.invitationId || undefined, undefined, CallRejectReason.missed, newCallState.roomId || undefined);
                            } catch (error) {
                                console.error("[useCallStore] Errore durante il rejectCall per timeout connecting:", error);

                                // Tentativo di leave della chiamata per timeout durante connecting, se è ancora in connecting dopo il timeout e se è la stessa chiamata (stesso invitationId)
                                try {
                                    await newCallState.leaveCall();
                                } catch (error) {
                                    console.error("[useCallStore] Errore durante il leaveCall per timeout connecting:", error);
                                }
                            }

                        }
                    }, CONNECTING_CALL_TIMEOUT_MS);
                }
                break;
            case CallStatus.waiting:
                // In attesa di altri partecipanti

                // se si passa da in-call a waiting, significa che gli altri partecipanti sono usciti
                if (prevStatus === CallStatus.inCall) {
                    // verifica se è una chiamata di gruppo
                    const groupCall = callState.isGroupCall;
                    // se non è una chiamata di gruppo, allora è una chiamata 1:1
                    // in caso di chiamata 1:1, esce dalla chiamata 
                    if (!groupCall) {
                        const waitingInvitationId = callState.invitationId;
                        startCallbackTimeout(async () => {
                            const newCallState = get();
                            // Se la chiamata è ancora in arrivo dopo il timeout, considerala finita
                            if (
                                newCallState.inCall &&
                                newCallState.callStatus === CallStatus.waiting &&
                                newCallState.invitationId === waitingInvitationId
                            ) {
                                console.debug("[useCallStore] Timeout durante la fase di waiting.", {
                                    inCall: newCallState.inCall,
                                    callStatus: newCallState.callStatus,
                                    isIncomingCall: newCallState.isIncomingCall,
                                    invitationId: newCallState.invitationId,
                                    waitingInvitationId
                                });
                                // Tentativo di leave della chiamata per timeout durante waiting, 
                                // se è ancora in waiting dopo il timeout e se è la stessa chiamata (stesso invitationId)
                                try {
                                    await newCallState.leaveCall(CallLeaveReason.exited);
                                } catch (error) {
                                    console.error("[useCallStore] Errore durante il leaveCall per timeout waiting:", error);
                                }

                            }
                        }, WAITING_1_1_CALL_TIMEOUT_MS);

                    } else {
                        // in caso di chiamata di gruppo rimane in waiting senza suonare
                    }
                }
                break;
            case CallStatus.inCall:
                // Chiamata attiva con almeno 2 partecipanti

                // Reset del timeout per chiamata senza risposta, se attivo
                clearCallbackTimeout();

                // Ferma tutte le suonerie in caso siano in riproduzione
                ringSource.stopAll(true);

                // Imposta il timestamp di inizio chiamata se non già impostato
                if (callState.start === null) {
                    callState.setStart(Date.now());
                    callState.setEnd(null);
                }
                break;
            case CallStatus.ended:
                // Chiamata terminata

                // Ferma tutte le suonerie in caso siano in riproduzione
                ringSource.stopAll(true);

                ringSource.play("callend");

                // Imposta il timestamp di fine chiamata
                if (callState.start !== null && callState.end === null) {
                    callState.setEnd(Date.now());
                }

                break;
            case CallStatus.error:
                // Errore nella chiamata

                // Ferma tutte le suonerie in caso siano in riproduzione
                ringSource.stopAll(true);

                // Riproduci suono di errore
                ringSource.play("error");

                let title: string = "";
                let errorMessage: string = "";
                if (reason instanceof HttpResponseError) {
                    console.error("[useCallStore] HttpResponseError:", reason);
                    // "error": {
                    //     "code": "CAL-013",
                    //         "message": "All participants are currently in a call"
                    // }
                    if (reason.statusCode === 409 && reason.response?.data?.error?.code === "CAL-013") {
                        title = _t("error.call.call_busy_title");
                        errorMessage = callState.isGroupCall ? _t("error.call.all_users_busy") : _t("error.call.user_busy");
                    }
                } else if (reason instanceof PublishTrackError) {
                    // IGNORARE QUESTI ERRORI
                }
                else {
                    title = _t("common.error");
                    errorMessage = _t("error.call.generic_call_error_message");
                }

                if (title || errorMessage) {
                    // Mostra un messaggio di errore generico
                    Modal.createDialog(ErrorDialog, {
                        title: title,
                        description: errorMessage,
                    });
                }

                // Imposta il timestamp di fine chiamata
                if (callState.start !== null && callState.end === null) {
                    callState.setEnd(Date.now());
                }
                break;
            default:
                break;
        }
    },
    createCall: async (participantsId, room) => {
        // Se non è stata passata una room come parametro, prova a recuperarla se è una DM (2 partecipanti)
        let currentRoom: Room | null = room || null;
        console.debug("[useCallStore] Creating call for room:", currentRoom?.roomId || "<no room>", "with participants:", participantsId);
        const callState = get();
        if (callState.inCall) {
            console.warn("[useCallStore] Already in a call, cannot create another.");
            return;
        }

        // Verifica permessi microfono prima di creare la chiamata
        if (!(await checkMicrophonePermissionsGranted())) {
            console.warn("[useCallStore] Microphone permission not granted.");
            Modal.createDialog(ErrorDialog, {
                title: _t("common.error"),
                description: _t("error.microphone_permission_required_description"),
            });

            return;
        }

        try {
            const privateConfiguration = DthSessionStorage.getPrivateConfiguration()!;
            const allowAudioCalls = privateConfiguration?.allowAudioCalls ?? false;
            const allowVideoCalls = privateConfiguration?.allowVideoCalls ?? false;

            if (!allowAudioCalls) {
                console.warn("[useCallStore] Audio calls are not allowed.");
                return;
            }

            const dthClient = DthHttpClient.getInstance();
            const myMatrixId = DthSessionStorage.getGenericItem(API_MATRIX_NAME_KEY)!;
            const myParticipantId = matrixIdToParticipantId(myMatrixId);
            // Ottieni gli ID dei partecipanti in formato participant ID @{participantId}:domain
            // Dovrebbero già essere in questo formato, ma li converto comunque per sicurezza e per evitare problemi in caso vengano passati in formato matrixId
            participantsId = participantsId.map(id => matrixIdToParticipantId(id));
            // Rimuovi se stesso dalla lista dei partecipanti
            const filteredParticipantsId = participantsId.filter((id: string) => id !== myParticipantId);

            // Imposta i partecipanti della chiamata (escluso se stesso)
            // prima di setInCall a true, per mostrare subito i nomi dei partecipanti nella UI della chiamata in uscita
            callState.setOtherParticipantIds(new Set(filteredParticipantsId));
            // Imposta lo stato di chiamata in corso
            callState.setInCall(true);
            // Imposta lo stato di chiamata in uscita
            callState.setCallStatus(CallStatus.outgoingRinging);
            // Imposta roomId della chiamata
            callState.setRoomId(currentRoom?.roomId || null);
            // Imposta se è chiamata di gruppo o 1:1
            callState.setIsGroupCall(filteredParticipantsId.length > 1);
            // Imposta chiamata come non in arrivo
            callState.setIsIncomingCall(false);
            // Imposta lo stato di connessione
            callState.setCallStatus(CallStatus.connecting);

            // Crea la stanza di chiamata tramite DTH, 
            // ottiene URL e token di LiveKit e altre info necessarie per gestire la chiamata
            const response = await dthClient.createEngagementRoom(filteredParticipantsId, allowVideoCalls);

            const liveKitUrl = toWssUrl(response.callUrl);
            console.debug("[useCallStore] LiveKit WSS URL:", liveKitUrl);
            const liveKitToken = response.token;
            console.debug("[useCallStore] LiveKit Token:", liveKitToken);
            const apiAccessToken = (await DthSessionStorage.getCredentials()).apiAccessToken;
            const liveKitKey = generateSecureRandomBytes(32);
            const liveKitKeyString = bytesToBase64(liveKitKey);
            console.debug("[useCallStore] Generated LiveKit Key:", bytesToBase64(liveKitKey));
            console.debug("[useCallStore] Generated LiveKit Key String:", liveKitKeyString);

            try {
                // Se non è stata passata una room come parametro, prova a recuperarla se è una DM (2 partecipanti)
                if (!currentRoom && response.room.participants.length! == 2) {
                    // se è una DM, recupera la roomId della stanza 1:1
                    const matrixName = response.room.participants.find(p => p.matrixName !== myMatrixId)?.matrixName;
                    if (matrixName) {
                        // Trova la room corrispondente al matrixName dell'altro partecipante
                        const roomFound: Room | null = findRoomByMatrixName(MatrixClientPeg.get()!, matrixName);
                        // Se trova la room, la imposta come room della chiamata
                        if (roomFound) {
                            // Imposta roomId della chiamata
                            currentRoom = roomFound;
                            callState.setRoomId(roomFound.roomId);
                        }
                    }
                }
            } catch (e) {
                console.warn("[useCallStore] Error finding matrix room for DM, proceeding without linking to a matrix room", e);
            }

            // Usa slice().buffer per ottenere un ArrayBuffer puro
            // const liveKitKeyBuffer = liveKitKey.slice().buffer;

            // Crea il LiveKitRoomManager
            const lkManager = new LiveKitRoomManager(
                callState.leaveCall,
                callState.addPresentParticipant,
                callState.removePresentParticipant
            );

            // Imposta lo stato della chiamata
            callState.setCallRoom({
                matrixRoom: currentRoom || null,
                engagementRoom: response.room,
                lkUrl: liveKitUrl,
                lkToken: liveKitToken,
                lkRoomManager: lkManager
            });

            // // Aggiungi utente corrente alla lista dei partecipanti presenti
            // if (myParticipantId) callState.addPresentParticipant({ identity: myParticipantId } as Participant);

            callState.dispatchCallAction({
                action: Action.ViewRoom,
                room_id: currentRoom?.roomId || null,
                liveKitData: {
                    lkUrl: liveKitUrl,
                    lkToken: liveKitToken,
                    lkRoomManager: lkManager,
                    roomId: currentRoom?.roomId || null,
                } as LiveKitData,
                showChatInCall: false,
                fallbackToHome: true
            });

            const channelManagers: ICallKeyExchangeManager[] = [];
            const connectPromises = response.channels.map((channel: string) => {
                const channelMaster = getCallKeyExchangeManagerInstance(CALL_VERSION, {
                    url: privateConfiguration.rtUrl,
                    token: apiAccessToken,
                    channel: channel,
                    role: "MASTER",
                    liveKitKey
                });
                channelManagers.push(channelMaster);
                return channelMaster.connectAndJoinChannel();
            });
            await Promise.all(connectPromises);
            callState.setChannelsManager([...callState.channelsManager, ...channelManagers]);

            await lkManager.connectRoom(
                liveKitUrl,
                liveKitToken,
                liveKitKeyString
            );

            // Imposta lo stato di connessione
            callState.setCallStatus(CallStatus.waiting);
        } catch (e: any) {
            console.error("[useCallStore] Error creating call:", e);
            callState.dispatchCallAction({
                action: Action.ViewRoom,
                room_id: currentRoom?.roomId || null,
                liveKitData: null,
                showChatInCall: false,
                fallbackToHome: true
            });

            callState.setInCall(false);
            callState.setCallStatus(CallStatus.error, e);
            callState.setCallStatus(CallStatus.idle);
            callState.setRoomId(null);
        }
    },
    joinCall: async (invitationId?: string): Promise<boolean> => {
        const callState = get();
        try {
            const lastInvitationId = invitationId || callState.invitationId || undefined;
            console.debug("[useCallStore] Joining call with invitation ID:", lastInvitationId);
            if (!lastInvitationId) {
                console.warn("[useCallStore] No invitation ID provided for joining call.");
                return false;
            }

            if (callState.callStatus !== CallStatus.incomingRinging) {
                console.warn("[useCallStore] Call is not in incoming ringing state, cannot join.");
                return false;
            }

            const privateConfiguration = DthSessionStorage.getPrivateConfiguration()!;
            const allowAudioCalls = privateConfiguration?.allowAudioCalls ?? false;
            // const allowVideoCalls = privateConfiguration?.allowVideoCalls ?? false;

            if (!allowAudioCalls) {
                console.warn("[useCallStore] Audio calls are not allowed.");
                return false;
            }

            // Verifica permessi microfono prima di creare la chiamata
            if (!(await checkMicrophonePermissionsGranted())) {
                console.warn("[useCallStore] Microphone permission not granted.");
                Modal.createDialog(ErrorDialog, {
                    title: _t("common.error"),
                    description: _t("error.microphone_permission_required_description"),
                });

                return false;
            }

            // Imposta lo stato di chiamata in corso
            callState.setInCall(true);
            callState.setCallStatus(CallStatus.connecting);
            if (!callState.isIncomingCall) callState.setIsIncomingCall(true);

            const dthClient = DthHttpClient.getInstance();
            let roomId: string | null = null;
            let matrixRoom: Room | null = null;
            let response: IDTHEngagementRoomResponse;
            try {
                response = await dthClient.joinEngagementRoom(lastInvitationId);
            } catch (e: any) {
                console.error("[useCallStore] Error joining engagement room:", e);
                callState.setInCall(false);
                callState.setCallStatus(CallStatus.error, e);
                callState.setCallStatus(CallStatus.idle);
                callState.setRoomId(null);
                return false;
            }

            try {
                if (response.room.participants.length === 2) {
                    // se è una DM, recupera la roomId della stanza 1:1
                    matrixRoom = findRoomByMatrixName(MatrixClientPeg.get()!, response.room.owner.matrixName);
                    roomId = matrixRoom?.roomId || null;
                }
            } catch (e) {
                console.error("[useCallStore] Error finding matrix room for DM, proceeding without linking to a matrix room", e);
            }

            // Imposta roomId della chiamata
            callState.setRoomId(roomId);

            // Imposta i partecipanti della chiamata (escluso se stesso)
            const myMatrixId: string | null = DthSessionStorage.getGenericItem(API_MATRIX_NAME_KEY)!;
            // const myParticipantId = matrixIdToParticipantId(myMatrixId);
            const otherParticipantIds = response.room.participants
                .filter(p => p.matrixName !== myMatrixId)
                .map(p => matrixIdToParticipantId(p.matrixName));
            callState.setOtherParticipantIds(new Set(otherParticipantIds));

            // Imposta se è chiamata di gruppo o 1:1
            callState.setIsGroupCall(response.room.participants.length > 2);

            const liveKitUrl = toWssUrl(response.callUrl);
            console.debug("[useCallStore] LiveKit WSS URL:", liveKitUrl);
            const liveKitToken = response.token;
            console.debug("[useCallStore] LiveKit Token:", liveKitToken);
            const apiAccessToken = (await DthSessionStorage.getCredentials()).apiAccessToken;
            // const liveKitKey = generateSecureRandomBytes(32);
            // const liveKitKeyString = bytesToBase64(liveKitKey);
            // console.debug("[useCallStore] Generated LiveKit Key:", bytesToBase64(liveKitKey));
            // console.debug("[useCallStore] Generated LiveKit Key String:", liveKitKeyString);

            // Predisposizione della LiveKitRoomManager con callback per gestione partecipanti e leave call
            const lkManager = new LiveKitRoomManager(
                callState.leaveCall,
                callState.addPresentParticipant,
                callState.removePresentParticipant
            );

            // Imposta lo stato della chiamata
            callState.setCallRoom({
                matrixRoom: matrixRoom,
                engagementRoom: response.room,
                lkUrl: liveKitUrl,
                lkToken: liveKitToken,
                lkRoomManager: lkManager
            });

            const liveKitData = {
                lkUrl: liveKitUrl,
                lkToken: liveKitToken,
                lkRoomManager: lkManager,
                roomId: roomId,
            } as LiveKitData;

            // Mostra la vista della chiamata
            callState.dispatchCallAction({
                action: Action.ViewRoom,
                room_id: roomId,
                liveKitData: liveKitData,
                showChatInCall: false,
                fallbackToHome: true
            });

            const channelManagers: ICallKeyExchangeManager[] = [];
            const connectPromises = response.channels.map((channel: string) => {
                const channelMaster = getCallKeyExchangeManagerInstance(CALL_VERSION, {
                    url: privateConfiguration.rtUrl,
                    token: apiAccessToken,
                    channel: channel,
                    role: "PEER",
                    liveKitKey: undefined
                });
                channelManagers.push(channelMaster);
                channelMaster.onKeyExchangeCompleted = async (info: CallKeyExchangeCompletedInfo) => {
                    try {
                        const liveKitKeyString = bytesToBase64(info.liveKitKey);
                        console.debug("[useCallStore] Generated LiveKit Key:", bytesToBase64(info.liveKitKey));
                        console.debug("[useCallStore] Generated LiveKit Key String:", liveKitKeyString);

                        const lkManager = get().callRoom!.lkRoomManager;

                        // Usa slice().buffer per ottenere un ArrayBuffer puro
                        // const liveKitKeyBuffer = liveKitKey.slice().buffer;
                        await lkManager.connectRoom(
                            liveKitUrl,
                            liveKitToken,
                            liveKitKeyString
                        );

                        // // Aggiungi utente corrente alla lista dei partecipanti presenti
                        // const myMatrixId = DthSessionStorage.getGenericItem(API_MATRIX_NAME_KEY)!;
                        // const myParticipantId = matrixIdToParticipantId(myMatrixId);
                        // callState.addParticipant({ identity: myParticipantId } as Participant);

                        // Imposta inCall a true
                        callState.setInCall(true);
                        // Imposta lo stato di waiting
                        callState.setCallStatus(CallStatus.waiting);

                        // callState.dispatchCallAction({
                        //     action: Action.ViewRoom,
                        //     room_id: roomId,
                        //     liveKitData: {
                        //         lkUrl: liveKitUrl,
                        //         lkToken: liveKitToken,
                        //         lkRoomManager: lkManager,
                        //         roomId: roomId,
                        //     } as LiveKitData,
                        //     showChatInCall: false,
                        //     fallbackToHome: true
                        // });

                    } catch (e: any) {
                        console.error("[useCallStore] Error connecting to LiveKit room:", e);
                        callState.setInCall(false);
                        callState.setCallStatus(CallStatus.error, e);
                        callState.setCallStatus(CallStatus.idle);
                        callState.setRoomId(null);

                        callState.dispatchCallAction({
                            action: Action.ViewRoom,
                            room_id: roomId,
                            liveKitData: null,
                            showChatInCall: false,
                            fallbackToHome: true
                        });
                    }
                };
                return channelMaster.connectAndJoinChannel();
            });
            await Promise.all(connectPromises);
            callState.setChannelsManager([...callState.channelsManager, ...channelManagers]);
        } catch (e) {
            console.error("[useCallStore] Error joining call:", e);
            if (callState.callStatus === CallStatus.incomingRinging) {
                callState.setInCall(false);
                callState.setCallStatus(CallStatus.error, e);
                callState.setCallStatus(CallStatus.idle);
                callState.setRoomId(null);
            }
        }
        return true;
    },
    rejectCall: async (
        invitationId?: string,
        callerName?: string,
        reason?: CallRejectReason,
        roomId?: string,
        showNotification?: boolean
    ): Promise<boolean> => {
        const callState = get();
        console.debug("[useCallStore] Rejecting call with invitation ID:", invitationId, "callerName:", callerName, "reason:", reason, "roomId:", roomId, "showNotification:", showNotification);

        // const privateConfiguration = DthSessionStorage.getPrivateConfiguration()!;
        // const allowAudioCalls = privateConfiguration?.allowAudioCalls ?? false;
        // const allowVideoCalls = privateConfiguration?.allowVideoCalls ?? false;

        // Se non viene specificato un motivo, assume che la chiamata sia stata rifiutata dall'utente
        const rejectReason = reason ?? CallRejectReason.refused;

        let result: boolean = true;
        let error: any = null;
        try {
            // Chiamata rifiutata dall'utente corrente
            if (rejectReason === CallRejectReason.refused) {
                const lastInvitationId = invitationId || callState.invitationId || undefined;
                console.debug("[useCallStore] Rejecting call with invitation ID:", lastInvitationId);
                const dthClient = DthHttpClient.getInstance();
                if (!lastInvitationId) {
                    console.warn("[useCallStore] No invitation ID provided for rejecting call.");
                } else {
                    result = await dthClient.rejectEngagementRoom(lastInvitationId);
                }
            }
        } catch (e: any) {
            console.error("[useCallStore] Error rejecting call:", e);
            error = e;
            result = false;
        } finally {
            if (showNotification) {
                // Mostra la notifica di chiamata persa o rifiutata
                mxNotifier.displayCustomPopupNotification({
                    title: callerName || "",
                    body: rejectReason == CallRejectReason.refused ? _t("room.call.refused_call") : _t("room.call.missed_call"),
                    roomId
                });
            }

            // Se l'invitationId corrisponde a quello della chiamata in corso, resetta lo stato
            if (invitationId == callState.invitationId) {
                callState.setInCall(false);
                if (!result) callState.setCallStatus(CallStatus.error, error);
                callState.setCallStatus(CallStatus.idle);
                callState.setRoomId(null);
            }
        }

        return result;
    },
    leaveCall: async (reason?: CallLeaveReason) => {
        console.debug("[useCallStore] Leaving call with reason:", reason);
        const callState = get();
        const { callRoom, channelsManager } = get();

        if (!callState.inCall) {
            console.warn("[useCallStore] Attempted to leave call when not in call");
            return;
        }

        if (callState.callStatus === CallStatus.idle) {
            console.warn("[useCallStore] Attempted to leave call when call status is idle");
            return;
        }

        try {
            const dthClient = DthHttpClient.getInstance();
            if (callRoom) {
                try {
                    await callRoom.lkRoomManager.disconnectRoom();
                } catch { }

                channelsManager.forEach((channelManager: ICallKeyExchangeManager) => {
                    try { channelManager.disconnect(); } catch { }
                });
                set({ channelsManager: [] });

                try {
                    // WORKAROUND PER RISOLVERE UN PROBLEMA DI GESTIONE NOTIFICHE SU IOS
                    // Se è una chiamata in uscita e non c'è ancora nessun partecipante (quindi ancora in waiting), 
                    // cancella la stanza di chiamata sul server per evitare che rimanga aperta inutilmente
                    // Negli altri casi, il server gestirà automaticamete la chiusura della stanza
                    if (!callState.isIncomingCall && callState.callStatus === CallStatus.waiting) {
                        await dthClient.cancelEngagementRoom(callRoom.engagementRoom.id);
                    }

                    let title = "";
                    let description = "";
                    // Mostra modale con messaggio del motivo chiamata chiusa
                    switch (reason) {
                        case CallLeaveReason.refused:
                            // Chiamata rifiutata dall'utente corrente
                            title = _t("room.call.callend_title");
                            // description = _t("room.call.refused_call_description");
                            description = _t("room.call.busy_call_description");
                            break;
                        case CallLeaveReason.noresponse:
                            // Chiamata persa (non risposta)
                            title = _t("room.call.callend_title");
                            description = _t("room.call.noresponse_call_description");
                            break;
                        case CallLeaveReason.exited:
                            // Uscita volontaria dall'altro utente dalla chiamata (chiamata 1 a 1)
                            title = _t("room.call.callend_title");
                            description = _t("room.call.exited_call_description");
                            break;
                        default:
                            break;
                    }

                    if (title || description) {
                        Modal.createDialog(ErrorDialog, {
                            title,
                            description,
                        });
                    }
                } catch { }

                callState.dispatchCallAction({
                    action: Action.ViewRoom,
                    room_id: callRoom.matrixRoom?.roomId ?? null,
                    liveKitData: null,
                    showChatInCall: false,
                    fallbackToHome: true
                });
            }
        } finally {
            // Reset stato chiamata a idle dopo aver eseguito tutte le operazioni di pulizia
            if (callState.callRoom != null) callState.setCallRoom(null);
            if (callState.inCall) {
                callState.setInCall(false);
                callState.setRoomId(null);
                callState.setCallStatus(CallStatus.ended);
                callState.setCallStatus(CallStatus.idle);
            }
        }

    },
    toggleMic: async () => {
        const { callRoom, micOn/*, setMicOn */ } = get();
        if (!callRoom || !callRoom.lkRoomManager) return;
        await callRoom.lkRoomManager.toggleMicrophone(!micOn);
        set({ micOn: !micOn });
        // setMicOn(!micOn);

    },
    toggleCam: async () => {
        const { callRoom, camOn/*, setCamOn */ } = get();
        if (!callRoom || !callRoom.lkRoomManager) return;
        await callRoom.lkRoomManager.toggleCamera(!camOn);
        set({ camOn: !camOn });
        // setCamOn(!camOn);
    },
    /**
     * Mostra una notifica di chiamata in arrivo.
     * @param invitationId ID dell'invito alla chiamata
     * @param callerName Nome di chi sta chiamando
     * @param roomId Room Matrix dove aprire la chat al click
     */
    triggerIncomingCall: (invitationId: string, callerName: string, roomId?: string) => {
        const callState = get();
        if (callState.inCall) {
            console.debug("[useCallStore] Already in a call, ignoring incoming call notification.");
            return;
        }
        // const room = MatrixClientPeg.get()!.getRoom(roomId);
        console.debug("[useCallStore] Triggering incoming call notification for invitation ID:", invitationId);
        // Salva l'ID dell'invito
        callState.setInvitationId(invitationId);
        // Imposta lo stato di chiamata in arrivo
        callState.setInCall(true);
        // Salva il nome di chi sta chiamando
        callState.setCallerName(callerName);
        if (roomId) callState.setRoomId(roomId);
        // Imposta lo stato di chiamata in arrivo
        callState.setIsIncomingCall(true);
        // Imposta lo stato di chiamata in arrivo
        callState.setCallStatus(CallStatus.incomingRinging);
        // // Se siamo già in chiamata o stiamo già squillando, non fare nulla
        // if (callState.inCall || callState.callStatus === CallStatus.ringing) {
        //     return;
        // }

        // Mostra la notifica di chiamata in arrivo
        mxNotifier.displayCustomPopupNotification({ title: callerName, body: _t("room.call.incoming_call"), roomId });
    },
    dispatchCallAction: (payload: CallDispatchPayload) => {
        // Se l'azione contiene showChatInCall, aggiorna lo stato prima di dispatchare l'azione, 
        // ma senza triggerare un ulteriore dispatch per evitare loop infiniti
        if (payload.showChatInCall != null) {
            get().setShowChatInCall(payload.showChatInCall, false);
        }
        defaultDispatcher.dispatch(payload, true);
    },
    setShowChatInCall: (showChatInCall, triggerDispatch = true) => {
        set({ showChatInCall });

        // Se triggerDispatch è true, dispatcha l'azione per aggiornare la visualizzazione della room
        if (triggerDispatch) {
            const callState = get();
            // Aggiorna la visualizzazione della room per 
            // mostrare/nascondere la chat durante la chiamata
            callState.dispatchCallAction({
                action: Action.ViewRoom,
                room_id: callState.roomId,
                showChatInCall: showChatInCall,
                fallbackToHome: true
            });
        }
    },
    goToCallRoomView: () => {
        const callState = get();

        // mostra la room della chiamata se è attiva, altrimenti mostra la room corrente
        callState.setShowChatInCall(false);
    },
    resetCallState: () => {
        const callState = get();
        if (callState.inCall) callState.setInCall(false);
        if (callState.callStatus !== CallStatus.idle) callState.setCallStatus(CallStatus.idle);
        if (callState.invitationId) callState.setInvitationId(null);

        if (callState.callRoom) {
            // Chiudi la connessione alla stanza LiveKit in modo asincrono,
            // ma non bloccare il reset dello stato chiamata
            const callRoom: CallRoomMetadata = callState.callRoom;
            (async () => {
                try {
                    await callRoom?.lkRoomManager.disconnectRoom();
                } catch (e) {/* gestisci l'errore se necessario */ }
            })();
            callState.setCallRoom(null);
        }

        // Reset timestamps
        if (callState.start !== null) callState.setStart(null);
        if (callState.end !== null) callState.setEnd(null);

        if (callState.channelsManager.length > 0) {
            callState.channelsManager.forEach((channelManager: ICallKeyExchangeManager) => {
                try { channelManager.disconnect(); } catch { }
            });
            set({ channelsManager: [] });
        }

        if (callState.roomId) callState.setRoomId(null);
        if (callState.isGroupCall !== null) callState.setIsGroupCall(null);
        if (callState.isIncomingCall !== null) callState.setIsIncomingCall(null);
        if (callState.callerName !== null) callState.setCallerName(null);
        if (callState.otherParticipantIds.size > 0) callState.setOtherParticipantIds(new Set<string>());
        if (callState.presentParticipantIds.size > 0) callState.setPresentParticipantIds(new Set<string>());
        if (callState.showChatInCall) callState.setShowChatInCall(false, false);

        // Chiudi PiP se è aperto
        callState.togglePiP(false);
    }
}));

/**
 * Bridge per usare useCallStore in un componente classe
 * @param props
 * @returns
 */
export function CallStoreBridge(props: { onInCallChange: (inCall: boolean) => void }) {
    const inCall = useCallStore(state => state.inCall);
    useEffect(() => {
        props.onInCallChange(inCall);
    }, [inCall]);
    return null;
}